import { useEffect, useRef, useState, type FormEvent } from "react";
import { askContext, askSupervisor, extrasOf, type ChatAction, type ChatActionDone, type ChatCard, type ChatChart, type ChatExport, type ChatMessage, type ChatQuestion, type ChatSide } from "../../lib/chat";
import { ConfirmAction } from "./ConfirmAction";
import { ChatText } from "./ChatText";
import { SideWindow } from "./SideWindow";
import { FirstSteps } from "./FirstSteps";
import { firstStepsDone, loadFirstSteps, type FirstStep } from "../../lib/firstSteps";
import type { JsonObject } from "../../lib/types";
import { useDictation } from "../../lib/speech";
import "./assistant.css";
import { Link, useLocation } from "react-router-dom";
import { ArrowUp, ChevronRight, Mic, Square } from "lucide-react";
import { PINS_CHANGED } from "../../lib/askOgma";
import { MetricChart } from "../MetricChart";
import { downloadCsv, toCsv } from "../../lib/csv";
import { env, functionUrl } from "../../lib/env";
import { httpFetch } from "../../lib/http";
import { useAuth, useWorkspace } from "../../lib/session";
import { humanise } from "../../lib/format";

const SUGGESTIONS = ["What needs my attention today?", "How is the pipeline moving this week?", "Export this week's sends as a spreadsheet"];

/**
 * The conversation with Ogma (the supervisor function: read-only tools, charts, exports and cards to pages), shown in the drawer beside
 * any page and as the full Ask Ogma page (Craig, 30 Oct 2026). `asked`: a question to send as soon as it is shown (from
 * the Ask bar, or Ask Ogma's own box); each id is sent once. `active`: shown now (the drawer when open, the page always).
 */
export function Chat({ active, asked = null, inputId, resume = null, firstSteps = false, page = false, onConversation }: { active: boolean; asked?: { id: number; text: string } | null; inputId: string;
  /** One of the person's past conversations to carry on (Ask Ogma › Past chats). */
  resume?: string | null;
  /** Show a new business's first steps on top (Ask Ogma's own page, 31 Oct 2026; lib/firstSteps.ts). */
  firstSteps?: boolean;
  /** The full Ask Ogma page (31 Oct 2026): laid out like Claude, a greeting and the box in the middle until the first question. */
  page?: boolean;
  /** Told the conversation's id once it is kept, so the page's address can come back to it (Craig, 1 Nov 2026). */
  onConversation?: (id: string) => void }) {
  const { session } = useAuth();
  const { crm, tenant } = useWorkspace();
  // Where the person asks from: the page, and the campaign or firm in view (Ask Ogma everywhere, 30 Oct 2026)
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // The conversation kept in the person's history (3.12), so the next answer adds to it
  const [conversation, setConversation] = useState<string | null>(null);
  const [files, setFiles] = useState<Record<number, ChatExport[]>>({});
  // Connect package 5: charts from get_metric, by the index of the reply that carried them, with the question asked
  const [charts, setCharts] = useState<Record<number, { question: string; list: ChatChart[] }>>({});
  const [pinned, setPinned] = useState<Record<string, string>>({});
  // Ask Ogma's cards: the pages an answer points to and the firms found, by the index of the reply (30 Oct 2026)
  const [cards, setCards] = useState<Record<number, ChatCard[]>>({});
  // The one thing an answer offers to do, by the index of the reply: done only on Do it
  const [actions, setActions] = useState<Record<number, ChatAction>>({});
  // What each offer did, kept on its answer (record_ask_action), so a chat opened again shows the search done
  const [done, setDone] = useState<Record<number, ChatActionDone>>({});
  const latest = useRef<{ messages: ChatMessage[]; conversation: string | null }>({ messages: [], conversation: null });
  // Ask Ogma as your assistant (30 Oct 2026): the thing beside the chat (the latest table, chart, draft or firm), and the
  // question each answer asked, by the index of the reply
  const [side, setSide] = useState<ChatSide | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [questions, setQuestions] = useState<Record<number, ChatQuestion>>({});
  // Replies whose table went to the side window: their preview is not repeated in the answer
  const [tablesBeside, setTablesBeside] = useState<Record<number, true>>({});
  // Speaking instead of typing: the browser's own speech to text, into the box, sent as text
  const dictation = useDictation((t) => setDraft((d) => (d ? `${d} ${t}` : t)));
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // A new business's first steps, read again after each answer and when the side window closes (a step done there)
  const [steps, setSteps] = useState<FirstStep[] | null>(null);
  const [stepsTick, setStepsTick] = useState(0);
  const config = (tenant?.config ?? null) as JsonObject | null;
  useEffect(() => {
    if (!firstSteps || !crm) return;
    let live = true;
    void loadFirstSteps(crm, config).then((s) => live && setSteps(s), () => undefined);
    return () => {
      live = false;
    };
  }, [firstSteps, crm, config, stepsTick, messages.length, sideOpen]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // preventScroll: the page keeps its own scroll position
    if (active) inputRef.current?.focus({ preventScroll: true });
  }, [active]);
  useEffect(() => {
    if (messages.length) endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages, busy]);
  useEffect(() => {
    // the conversation this chat already shows (its new id put in the address) is not read again
    if (!resume || !crm || resume === latest.current.conversation) return;
    let live = true;
    void crm.read<{ id: string; messages: ChatMessage[] } | null>("crm_ask_conversation", { p_conversation: resume }).then((c) => {
      if (!live || !c) return;
      setMessages(c.messages);
      setConversation(c.id);
      // A past chat shows each answer as it was given: its cards, offer, question and charts, and its side window to reopen
      const kept = { cards: {} as Record<number, ChatCard[]>, actions: {} as Record<number, ChatAction>, done: {} as Record<number, ChatActionDone>,
        questions: {} as Record<number, ChatQuestion>,
        charts: {} as Record<number, { question: string; list: ChatChart[] }> };
      let lastSide: ChatSide | null = null;
      c.messages.forEach((m, i) => {
        if (m.role !== "assistant") return;
        const x = extrasOf(m);
        if (x.cards.length) kept.cards[i] = x.cards;
        if (x.action) kept.actions[i] = x.action;
        if (x.done) kept.done[i] = x.done;
        if (x.question) kept.questions[i] = x.question;
        if (x.charts.length) kept.charts[i] = { question: c.messages[i - 1]?.content ?? "", list: x.charts };
        if (x.side) lastSide = x.side;
      });
      setCards(kept.cards);
      setActions(kept.actions);
      setDone(kept.done);
      setQuestions(kept.questions);
      setCharts(kept.charts);
      if (lastSide) setSide(lastSide);
    }, () => undefined);
    return () => {
      live = false;
    };
  }, [resume, crm]);
  latest.current = { messages, conversation };
  useEffect(() => {
    if (conversation && conversation !== resume) onConversation?.(conversation);
    // only a newly kept conversation is reported
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onConversation is the page's handler; only the id matters
  }, [conversation]);

  // Keeps what an offer did on its answer, counted from the end as the kept chat may have dropped its start (40 messages)
  const keepDone = (i: number, d: ChatActionDone) => {
    setDone((x) => ({ ...x, [i]: d }));
    const { messages: now, conversation: id } = latest.current;
    if (!crm || !id) return;
    void crm.write("record_ask_action", { conversation: id, from_end: now.length - 1 - i, done: d }).catch(() => undefined);
  };

  const sentAsk = useRef(0);
  useEffect(() => {
    // wait for the session (a question handed over as the page loads), so it is never dropped
    if (!active || !asked || asked.id === sentAsk.current || !session || busy) return;
    sentAsk.current = asked.id;
    void send(asked.text);
    // send reads the latest messages; only a new question should trigger this
    // eslint-disable-next-line react-hooks/exhaustive-deps -- send reads the latest messages; only a new question should send
  }, [active, asked, session, busy]);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || busy || !session) return;
    const next: ChatMessage[] = [...messages, { role: "user", content }];
    setMessages(next);
    setDraft("");
    setBusy(true);
    setProblem(null);
    if (dictation.listening) dictation.stop();
    const r = await askSupervisor({ url: functionUrl("supervisor"), token: session.access_token, anonKey: env.supabaseAnonKey, messages: next,
      context: askContext(location.pathname, location.search), conversation, side: sideOpen ? side : null, fetchImpl: httpFetch });
    setBusy(false);
    if (r.ok) {
      setMessages([...next, { role: "assistant", content: r.text }]);
      if (r.conversation) setConversation(r.conversation);
      if (r.exports.length) setFiles((f) => ({ ...f, [next.length]: r.exports }));
      if (r.charts.length) setCharts((c) => ({ ...c, [next.length]: { question: content, list: r.charts } }));
      if (r.cards.length) setCards((c) => ({ ...c, [next.length]: r.cards }));
      if (r.action) setActions((a) => ({ ...a, [next.length]: r.action! }));
      if (r.question) setQuestions((q) => ({ ...q, [next.length]: r.question! }));
      if (r.side) {
        if (r.side.kind === "table") setTablesBeside((t) => ({ ...t, [next.length]: true }));
        setSide(r.side);
        setSideOpen(true);
      }
    }
    else {
      // Taken back into the box to try again: an unanswered question left in the history gets answered alongside the
      // next one (31 Oct 2026: the Italy search came back under a question about Nina).
      setMessages(messages);
      setDraft(content);
      setProblem(r.text);
    }
  };

  // Pin a chart to Sales performance (act pin_performance, as the signed-in person); the page reloads its pins.
  const pin = async (key: string, c: ChatChart, question: string) => {
    if (!crm) return;
    setPinned((p) => ({ ...p, [key]: "Pinning…" }));
    try {
      // without the model's dates, so the pin follows the period chosen on the page
      const { from: _from, to: _to, ...spec } = c.spec;
      await crm.pinPerformance(c.title, spec, question);
      setPinned((p) => ({ ...p, [key]: "Pinned to Sales performance. It follows the period you pick there." }));
      window.dispatchEvent(new Event(PINS_CHANGED));
    } catch (e) {
      setPinned((p) => ({ ...p, [key]: e instanceof Error ? e.message : "That couldn't be pinned." }));
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(draft);
  };

  const empty = messages.length === 0 && !busy && !(steps && !firstStepsDone(steps));
  const body = (
    <>
      <div className="chat-log" aria-live="polite">
        {steps && !firstStepsDone(steps) && (
          <FirstSteps steps={steps} onAsk={(t) => void send(t)} onChanged={() => setStepsTick((t) => t + 1)}
            onOpenPage={(path, title) => {
              setSide({ kind: "page", path, title });
              setSideOpen(true);
            }} />
        )}
        {empty && page && (
          <div className="chat-hello">
            <h2>What can I help with?</h2>
          </div>
        )}
        {empty && !page && (
          <div className="chat-intro">
            <p>Ask about your pipeline, what's waiting, or ask for an export. Ogma only sees your business.</p>
            <ul className="chat-suggestions">
              {SUGGESTIONS.map((s) => (
                <li key={s}>
                  <button type="button" className="link-btn" onClick={() => void send(s)}>
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`bubble bubble-${m.role}`}>
            <span className="sr-only">{m.role === "user" ? "You said" : "Ogma said"}</span>
            {m.role === "assistant" ? <ChatText text={m.content} /> : m.content}
            {questions[i] && i === messages.length - 1 && !busy && questions[i]!.options.length > 0 && (
              <div className="chat-answers" role="group" aria-label={questions[i]!.text}>
                {/* Stacked rows that read as choices (Craig, 1 Nov 2026: the small quiet buttons didn't look like options) */}
                {questions[i]!.options.map((o) => (
                  <button key={o} type="button" className="chat-answer" onClick={() => void send(o)}>
                    <span>{o}</span>
                    <ChevronRight size={16} aria-hidden="true" focusable="false" />
                  </button>
                ))}
              </div>
            )}
            {side && !sideOpen && i === messages.length - 1 && m.role === "assistant" && (
              <button type="button" className="link-btn small" onClick={() => setSideOpen(true)}>Show it again</button>
            )}
            {actions[i] && <ConfirmAction action={actions[i]!} done={done[i] ?? null} onDone={(d) => keepDone(i, d)} onAsk={(t) => void send(t)} onOpenPage={(path, title) => {
              setSide({ kind: "page", path, title });
              setSideOpen(true);
            }} />}
            {cards[i] && (
              <ul className="chat-cards" aria-label="Where to go">
                {cards[i]!.map((c) => (
                  <li key={c.to}>
                    {/* Opens beside the chat (Craig, 31 Oct 2026: never leave the chat); a new tab or the side window's Full page leaves it */}
                    <Link to={c.to} className={`chat-card chat-card-${c.kind}`}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        setSide({ kind: "page", path: c.to, title: c.title });
                        setSideOpen(true);
                      }}>
                      <strong>{c.title}</strong>
                      {c.detail && <span className="muted small">{c.detail}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {charts[i]?.list.map((c, j) => {
              const key = `${i}-${j}`;
              return (
                <figure key={key} className="chat-chart">
                  <figcaption className="small">
                    <strong>{c.title}</strong>
                  </figcaption>
                  <MetricChart result={c.result} caption={c.title} />
                  <p className="small muted">{c.summary}</p>
                  {crm && (
                    <button type="button" className="btn btn-quiet" onClick={() => void pin(key, c, charts[i]!.question)} disabled={Boolean(pinned[key]?.startsWith("Pinn"))}>
                      Pin to Sales performance<span className="sr-only">: {c.title}</span>
                    </button>
                  )}
                  {pinned[key] && (
                    <p className="small" role="status">
                      {pinned[key]}
                    </p>
                  )}
                </figure>
              );
            })}
            {!tablesBeside[i] && files[i]?.map((x) => (
              <div key={`${x.filename}-table`} className="chat-table">
                {x.rows.length > 0 && (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          {x.columns.slice(0, 4).map((c) => (
                            <th key={c} scope="col">
                              {humanise(c)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {x.rows.slice(0, 5).map((r, j) => (
                          <tr key={j}>
                            {x.columns.slice(0, 4).map((c) => (
                              <td key={c}>{r[c] === null || r[c] === undefined ? "—" : typeof r[c] === "boolean" ? (r[c] ? "Yes" : "No") : String(r[c])}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="small muted">
                  {x.rows.length > 5 ? `First 5 of ${x.rows.length} rows. ` : ""}
                  {x.columns.length > 4 ? "The download has every column." : ""}
                </p>
              </div>
            ))}
            {files[i]?.map((x) => (
              <button
                key={x.filename}
                type="button"
                className="btn btn-quiet chat-file"
                onClick={() => downloadCsv(x.filename.endsWith(".csv") ? x.filename : `${x.filename}.csv`, toCsv(x.rows, x.columns.map((c) => ({ header: c, value: (r: Record<string, unknown>) => r[c] }))))}
              >
                Download {x.filename}
              </button>
            ))}
          </div>
        ))}
        {busy && (
          <div className="bubble bubble-assistant bubble-wait" role="status">
            Thinking…
          </div>
        )}
        {problem && (
          <p className="notice notice-bad" role="alert">
            {problem}
          </p>
        )}
        <div ref={endRef} />
      </div>
      <form className="chat-form" onSubmit={onSubmit}>
        <label htmlFor={inputId} className="sr-only">
          Message
        </label>
        <textarea
          id={inputId}
          ref={inputRef}
          rows={2}
          value={dictation.listening && dictation.interim ? `${draft}${draft ? " " : ""}${dictation.interim}` : draft}
          readOnly={dictation.listening}
          placeholder={page ? "Ask anything, or tell Ogma what to do" : "Ask anything about your business"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send(draft);
            }
          }}
        />
        {dictation.supported && (page ? (
          <button type="button" className={`chat-icon-btn btn-talk${dictation.listening ? " is-listening" : ""}`} aria-pressed={dictation.listening}
            aria-label={dictation.listening ? "Stop" : "Speak"} onClick={dictation.listening ? dictation.stop : dictation.start}>
            {dictation.listening ? <Square size={16} aria-hidden="true" focusable="false" /> : <Mic size={18} aria-hidden="true" focusable="false" />}
          </button>
        ) : (
          <button type="button" className={`btn btn-quiet btn-talk${dictation.listening ? " is-listening" : ""}`} aria-pressed={dictation.listening}
            onClick={dictation.listening ? dictation.stop : dictation.start}>
            {dictation.listening ? "Stop" : "Speak"}
          </button>
        ))}
        {page ? (
          <button type="submit" className="chat-send" aria-label="Send" disabled={busy || !draft.trim()}>
            <ArrowUp size={18} aria-hidden="true" focusable="false" />
          </button>
        ) : (
          <button type="submit" className="btn btn-primary" disabled={busy || !draft.trim()}>
            Send
          </button>
        )}
      </form>
      {empty && page && (
        <ul className="chat-chips" aria-label="Suggestions">
          {SUGGESTIONS.map((x) => (
            <li key={x}>
              <button type="button" className="chat-chip" onClick={() => void send(x)}>
                {x}
              </button>
            </li>
          ))}
        </ul>
      )}
      {dictation.error && <p className="small notice notice-bad" role="alert">{dictation.error}</p>}
      {side && sideOpen && <SideWindow side={side} onClose={() => setSideOpen(false)} onChange={setSide} onAsk={(t) => void send(t)} />}
    </>
  );
  return page ? <div className={`chat-page${empty ? " is-empty" : ""}`}>{body}</div> : body;
}
